import { useEffect, useMemo } from "react";
import {
    Controller,
    useFieldArray,
    useFormContext,
    useFormState,
    useWatch,
} from "react-hook-form";
import { useQuery } from "@tanstack/react-query";
import { AsyncPaginate } from "react-select-async-paginate";
import FormSelect from "../shared/FormSelect";
import {
    LabelValue,
    ListSection,
    PARTIAL_INTERVAL_FIELD_CONFIG,
    PARTIAL_INTERVAL_OPTIONS,
    PARTIAL_INTERVAL_WASH,
    billingRateRowToCells,
    buildBillingRateRows,
    formatLockDate,
    getBillingRateHeaders,
    partialIntervalMethodValue,
} from "./BillingRateBuilderShared";
import { billingCategoryRateSchema } from "./billingRateBuilderSchemas";
import { loadAccountHccIdentificationNumber } from "../../utils/optionLoaderUtils";
import { customStyles } from "../../utils/styleUtils";
import { fetchBenefitPlansByAccountKey } from "../../utils/apiUtils";

const getDefMetaFromForm = (def) => ({
    name: def?.componentReference?.component?.value ?? "",
    lockDate: formatLockDate(def?.componentReference?.lockDate),
});

// Collects all validation messages for a category's billing rate definitions
// (list-level plus per-definition).
const getDefinitionErrorMessages = (catErrors) => {
    const defs = catErrors?.billingRateDefinitions;
    if (!defs) return [];
    const messages = [];
    const listMessage = defs.message ?? defs.root?.message;
    if (listMessage) messages.push(listMessage);
    if (Array.isArray(defs)) {
        defs.forEach((def) => {
            const componentMessage = def?.componentReference?.component?.message;
            const lockDateMessage = def?.componentReference?.lockDate?.message;
            if (componentMessage) messages.push(componentMessage);
            if (lockDateMessage) messages.push(lockDateMessage);
        });
    }
    return [...new Set(messages)];
};

// Converts a Zod issues array into the nested error object format that matches
// React Hook Form's errors shape (e.g. { billingCategory: { message } }).
const buildNestedErrors = (issues) => {
    const root = {};
    (issues ?? []).forEach((issue) => {
        if (issue.path.length === 0) return;
        let node = root;
        for (let i = 0; i < issue.path.length - 1; i++) {
            const key = issue.path[i];
            const nextKey = issue.path[i + 1];
            if (node[key] == null) {
                node[key] = typeof nextKey === "number" ? [] : {};
            }
            node = node[key];
        }
        const last = issue.path[issue.path.length - 1];
        if (node[last] == null) {
            node[last] = { message: issue.message, type: "custom" };
        }
    });
    return root;
};

const makeBlankCategory = () => ({
    billingCategory: null,
    partialIntervalCalculationMethod:
        PARTIAL_INTERVAL_OPTIONS.find((o) => o.value === PARTIAL_INTERVAL_WASH) ?? null,
    minimumEnrolledDaysForFullPeriodBilling: "",
    prorationByNumberOfDaysPerMonth: "",
    washDay: 15,
    billingPartyPercentages: [{ billingParty: "Account", billPercentage: 100 }],
    billingRateDefinitions: [],
});

const RateEditBody = ({
    componentsByName,
    billingCategoryRows,
    onEditCategory,
    onRegisterRemove,
    triggerValidationOnMount = false,
}) => {
    const { control, setValue, trigger } = useFormContext();
    const { errors } = useFormState({ control });

    // When editing an existing rate, surface validation errors immediately.
    useEffect(() => {
        if (triggerValidationOnMount) {
            trigger();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    const { fields, append, remove } = useFieldArray({
        control,
        name: "billingCategoryRates",
    });

    // Register the remove function so RateEditForm can call it when the user
    // cancels adding a new category (undoing the append before entering edit).
    useEffect(() => {
        onRegisterRemove?.(remove);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    const categories = useWatch({ control, name: "billingCategoryRates" }) ?? [];
    const account = useWatch({ control, name: "account" });
    const accountKey = account?.additional?.accountKey ?? null;

    const handleAddCategory = () => {
        const newIndex = fields.length;
        append(makeBlankCategory());
        onEditCategory?.(newIndex, true);
    };

    const handleDeleteCategory = (index) => {
        remove(index);
    };

    const { data: benefitPlanRows } = useQuery({
        queryKey: ["billing-rate-edit-benefit-plans", accountKey],
        queryFn: () => fetchBenefitPlansByAccountKey({ accountKey }),
        enabled: Boolean(accountKey),
        staleTime: 0,
    });

    const benefitPlanOptions = useMemo(() => {
        const seen = new Set();
        return (benefitPlanRows ?? []).reduce((acc, row) => {
            if (!seen.has(row.benefitPlanName)) {
                seen.add(row.benefitPlanName);
                acc.push({
                    value: row.benefitPlanName,
                    label: `${row.benefitPlanName} (${row.benefitPlanHccId})`,
                    additional: { benefitPlanHccId: row.benefitPlanHccId },
                });
            }
            return acc;
        }, []);
    }, [benefitPlanRows]);

    return (
        <div className="billing-rate-builder__modal-content-body">
            <div className="billing-rate-builder__label-grid">
                <div className="form-input">
                    <label>Account</label>
                    <Controller
                        name="account"
                        control={control}
                        render={({ field }) => (
                            <AsyncPaginate
                                {...field}
                                placeholder="Select an account"
                                className="react-select-container"
                                classNamePrefix="react-select"
                                debounceTimeout={300}
                                loadOptions={loadAccountHccIdentificationNumber}
                                styles={customStyles}
                                isClearable
                                onChange={(option) => {
                                    field.onChange(option);
                                    setValue("benefitPlan", null, { shouldDirty: true });
                                }}
                            />
                        )}
                    />
                    {errors?.account?.message && (
                        <p style={{ color: "var(--red)", paddingTop: ".4rem" }}>
                            {errors.account.message}
                        </p>
                    )}
                </div>
                <FormSelect
                    name="benefitPlan"
                    label="Benefit Plan"
                    control={control}
                    options={benefitPlanOptions}
                    errors={errors}
                />
            </div>

            <div className="billing-rate-builder__section-header">
                <p className="title billing-rate-builder__section-title">Billing Category Rates</p>
                <button
                    type="button"
                    className="btn edit-btn"
                    onClick={handleAddCategory}
                >
                    + Add
                </button>
            </div>
            {(errors?.billingCategoryRates?.message ??
                errors?.billingCategoryRates?.root?.message) && (
                <p style={{ color: "var(--red)", paddingTop: ".4rem" }}>
                    {errors.billingCategoryRates.message ??
                        errors.billingCategoryRates.root?.message}
                </p>
            )}

            {fields.map((fld, i) => {
                const cat = categories[i] ?? {};
                const codeEntry = cat?.billingCategory?.value ?? "";
                const matchedCategory = (billingCategoryRows ?? []).find(
                    (r) => r?.cdEntryTxt === codeEntry,
                );
                const shortNm =
                    matchedCategory?.shortNm ?? cat?.billingCategory?.additional?.shortName ?? "";
                const cdEntryTxt = matchedCategory?.cdEntryTxt ?? codeEntry;
                const categoryFullName = shortNm
                    ? `${shortNm} (${cdEntryTxt})`
                    : codeEntry;
                const method = partialIntervalMethodValue(cat?.partialIntervalCalculationMethod);
                const activeFieldConfig = method ? PARTIAL_INTERVAL_FIELD_CONFIG[method] : null;
                const activeFieldLabel =
                    activeFieldConfig?.label ?? "Wash Day";
                const activeFieldValue = activeFieldConfig ? cat?.[activeFieldConfig.name] : "";
                const { rowsByDef, anyTiered } = buildBillingRateRows(
                    cat?.billingRateDefinitions,
                    componentsByName,
                    getDefMetaFromForm,
                );
                const rateHeaders = getBillingRateHeaders(anyTiered);
                const rateRows = rowsByDef
                    .flat()
                    .map((r) => billingRateRowToCells(r, anyTiered));
                // Validate each category independently against the full schema.
                // The form-level schema (rateHeaderSchema) only checks that at
                // least one category exists; per-field errors must be derived
                // separately so they are always visible in the summary cards.
                const parseResult = billingCategoryRateSchema.safeParse(cat);
                const catErrors = parseResult.success
                    ? {}
                    : buildNestedErrors(parseResult.error.issues);
                const categoryError = catErrors?.billingCategory?.message;
                const partialIntervalError = catErrors?.partialIntervalCalculationMethod?.message;
                const definitionErrors = getDefinitionErrorMessages(catErrors);
                return (
                    <div key={fld.id} className="billing-rate-builder__card">
                        <div className="billing-rate-builder__modal-header">
                            <p className="sub-title billing-rate-builder__branch-title">
                                {shortNm ? `Billing Rate Component - (${shortNm})` : "Billing Rate Component"}
                            </p>
                            <div className="billing-rate-builder__row-actions">
                                <button
                                    type="button"
                                    className="btn edit-btn"
                                    onClick={() => onEditCategory(i)}
                                >
                                    Edit
                                </button>
                                <button
                                    type="button"
                                    className="btn delete-btn"
                                    onClick={() => handleDeleteCategory(i)}
                                >
                                    Delete
                                </button>
                            </div>
                        </div>

                        <LabelValue
                            label="Billing Category"
                            value={categoryFullName}
                            error={categoryError}
                        />

                        <div className="billing-rate-builder__parameters-section">
                            <p className="sub-title billing-rate-builder__subtitle-emphasis">
                                Billing Parameters
                            </p>
                            <div className="billing-rate-builder__label-grid">
                                <LabelValue
                                    label="Partial Interval Calculation Method"
                                    value={method}
                                    error={partialIntervalError}
                                />
                                <LabelValue
                                    label={activeFieldLabel}
                                    value={activeFieldValue}
                                />
                            </div>
                        </div>

                        <ListSection
                            label="Billing Party Percentages"
                            headers={["Billing Party", "Bill Percentage"]}
                            rows={(cat?.billingPartyPercentages ?? []).map((bpp) => [
                                bpp?.billingParty,
                                bpp?.billPercentage,
                            ])}
                        />

                        {definitionErrors.map((msg, idx) => (
                            <p key={idx} style={{ color: "var(--red)", paddingTop: ".4rem", fontSize: ".85rem" }}>
                                {msg}
                            </p>
                        ))}

                        <ListSection
                            label="Billing Rates"
                            headers={rateHeaders}
                            rows={rateRows}
                        />
                    </div>
                );
            })}
        </div>
    );
};

export default RateEditBody;
